"use client";
import {
  Box,
  Button,
  createTheme,
  Grid,
  Skeleton,
  Stack,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import Card from "@/app/components/genericComponents/card/Card";
import ViewDetails from "@/app/components/genericComponents/view-details/ViewDetails";
import MobileCard from "@/app/components/genericComponents/card/MobileCard";
import MenuCategory from "@/app/components/genericComponents/menu/MenuCategory";
import ViewDetailsRsOneContainer from "@/app/components/genericComponents/view-details/ViewDetailsRsOneContainer";
import { BasicProgram, ViewDetailsCategory } from "@/app/classes/main";
import MakeYourOwnPackage from "@/app/components/genericComponents/view-details/MakeYourOwnPackage";
import { BestPairedDesktopWraper } from "@/app/components/genericComponents/card/BestPairedDesktopWraper";

interface ServiceViewContainerProps {
  subHeadings?: any;
  categories?: any;
  categorySlug?: any;
}

interface ViewCardsProps {
  subHeadings?: any;
  categories?: any;
  categorySlug?: any;
}

const ServiceViewContainer: React.FC<ServiceViewContainerProps> = ({
  subHeadings,
  categories,
  categorySlug,
}) => {
  return (
    <>
      <ViewCards
        subHeadings={subHeadings}
        categories={categories}
        categorySlug={categorySlug}
      />
    </>
  );
};

const ViewCards: React.FC<ViewCardsProps> = ({
  subHeadings,
  categories,
  categorySlug,
}) => {
  const [open, setOpen] = useState(false);
  const [openPackages, setOpenPackages] = useState(false);
  const [viewDetails, setViewDetails] = useState<
    ViewDetailsCategory | undefined
  >(undefined);
  const [updateCard, setUpdateCard] = useState(false);
  const [reloadViewDetailsItem, setReloadViewDetailsItem] = useState(false);
  const [openRsOneDetails, setOpenRsOneDetails] = useState(false);
  const [viewDetailsRsOneSid, setViewDetailsRsOneSid] = useState<
    number | undefined
  >(undefined);
  const [openBestPaired, setOpenBestPaired] = useState(false);
  const [recommendedServices, setRecommendedServices] = useState<
    [] | undefined
  >([]);

  const themeBreakPoint = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1350, // Adjust lg to 1400px
        xl: 1536,
      },
    },
  });
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const scrollToSection = (name: any) => {
    const element = document.getElementById(name);
    if (element) {
      const navbarHeight =
        (document.querySelector(".navbar-scroller") as HTMLElement)
          ?.offsetHeight ?? 0;
      window.scrollTo({
        top: element.offsetTop - navbarHeight,
        behavior: "smooth",
      });
    }
  };

  const handleViewDetails = (service: ViewDetailsCategory) => {
    setOpen(true);
    setViewDetails(service);
  };
  const handleEditPackage = (service: ViewDetailsCategory, index: number) => {
    setOpenPackages(true);
    setViewDetails(service);
  };

  const setClose = () => {
    setOpen(false);
  };

  const handleOpenViewDetailsRsOne = (
    basicProgram: BasicProgram,
    index: number
  ) => {
    setOpenRsOneDetails(true);
    setViewDetailsRsOneSid(basicProgram?.id);
  };

  const handleOpenViewDetails = (sid: any) => {
    setOpenRsOneDetails(true);
    setViewDetailsRsOneSid(sid);
  };

  return (
    <>
      <Box
        sx={{
          background: "#e6e3e390",
        }}
      >
        <Grid
          container
          item
          xs={12}
          sm={11}
          md={11}
          lg={9.5}
          margin="auto"
          style={{
            padding: "0.5rem 0px",
            justifyContent: "center",
          }}
        >
          {/* side bar */}
          <Grid container item xs={1} sm={1} md={1} lg={1} margin="auto">
            <Box>
              <MenuCategory
                categories={categories}
                categorySlug={categorySlug}
              />
            </Box>
          </Grid>
          {/* heading clickable scroll */}
          <Grid container item xs={11} sm={11} md={11} lg={11} margin="auto">
            <Box
              sx={{
                justifyContent: isSmallScreen ? "left" : "center",
                display: "flex",
                alignItems: "center",
                overflow: "auto",
                margin: isSmallScreen ? "0px 0px 0px 1.5rem" : "auto",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              {subHeadings?.map((item: any, index: number) => (
                <Button
                  key={index}
                  variant="outlined"
                  color="success"
                  sx={{
                    color: "#15741a",
                    margin: "auto 0.5rem",
                    textTransform: "capitalize",
                    textWrap: "nowrap",
                    padding: "3px 15px",
                    minWidth: "auto",
                  }}
                  onClick={() => scrollToSection(item.name)}
                >
                  {item?.name}
                </Button>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {subHeadings?.map((serviceHeading: any, index: number) => (
        <Box key={index}>
          <Stack sx={{ marginBottom: "1rem" }}>
            {/* Heading */}
            <Grid
              container
              item
              xs={11}
              sm={11}
              md={11}
              lg={9.5}
              margin="auto"
              style={{ padding: "1rem 0rem" }}
            >
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  width: "100%",
                  fontSize: { xs: "20px", sm: "32px" },
                }}
                id={serviceHeading?.name}
              >
                {serviceHeading.name}
              </Typography>
            </Grid>
            {/* Cards  */}
            <Grid container item xs={12} sm={11} md={11} lg={9.5} margin="auto">
              {serviceHeading
                ? serviceHeading &&
                  serviceHeading.services &&
                  serviceHeading.services.map((item: any, index: number) => (
                    <ThemeProvider theme={themeBreakPoint} key={index}>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={3}
                        sx={{ margin: { xs: "auto", sm: "0px" } }}
                      >
                        <Box sx={{ display: { xs: "none", sm: "block" } }}>
                          <Card
                            addOn={item?.addOn}
                            id={item.id}
                            cid={item.cid}
                            url={
                              item?.banner?.secureHttpPath ??
                              item.coverImage.secureHttpPath
                            }
                            heading={item.name}
                            subHeading={item.subHeading}
                            price={item.mrp}
                            discount={item.discount}
                            finalPrice={item.amount}
                            serviceDetails={item.aboutList}
                            handleViewDetails={() => handleViewDetails(item)}
                            updateCard={updateCard}
                            serviceCharge={item.serviceCharge}
                            productCost={item.productCost}
                            min={item.min}
                            specialPrice={item?.specialPrice}
                            specialMessage={item?.specialMessage}
                            setUpdateCard={() => setUpdateCard(false)}
                            reloadViewDetailsItem={() =>
                              setReloadViewDetailsItem(true)
                            }
                            productDetailsAvailable={
                              item?.productDetailsAvailable
                            }
                            handleOpenViewDetailsRsOne={() =>
                              handleOpenViewDetailsRsOne(item, index)
                            }
                            makeYourOwnPackage={false}
                            handleEditPackage={() =>
                              handleEditPackage(item, index)
                            }
                            recommendedServices={item?.recommendedServices}
                            setOpenBestPaired={setOpenBestPaired}
                            setRecommendedServices={(item: any) =>
                              setRecommendedServices(item)
                            }
                          />
                        </Box>
                        <Box sx={{ display: { xs: "block", sm: "none" } }}>
                          <MobileCard
                            addOn={item?.addOn}
                            id={item?.id}
                            cid={item?.cid}
                            url={item?.coverImage?.secureHttpPath}
                            bannerUrl={item?.banner?.secureHttpPath ?? ""}
                            heading={item?.name}
                            subHeading={item?.subHeading}
                            price={item?.mrp}
                            discount={item?.discount}
                            finalPrice={item?.amount}
                            serviceDetails={item?.aboutList}
                            min={item?.min}
                            coin={item?.cashbackSalontymCoins}
                            aboutList={item?.aboutList}
                            serviceCharge={item?.serviceCharge}
                            productCost={item?.productCost}
                            specialPrice={item?.specialPrice}
                            specialMessage={item?.specialMessage}
                            handleViewDetails={() => handleViewDetails(item)}
                            updateCard={updateCard}
                            setUpdateCard={() => setUpdateCard(false)}
                            reloadViewDetailsItem={() =>
                              setReloadViewDetailsItem(true)
                            }
                            productDetailsAvailable={
                              item?.productDetailsAvailable
                            }
                            handleOpenViewDetailsRsOne={() =>
                              handleOpenViewDetailsRsOne(item, index)
                            }
                            makeYourOwnPackage={false}
                            handleEditPackage={() =>
                              handleEditPackage(item, index)
                            }
                            recommendedServices={item?.recommendedServices}
                            handleViewDetailsBestPaired={(data: any) =>
                              handleViewDetails(data)
                            }
                          />
                        </Box>
                      </Grid>
                    </ThemeProvider>
                  ))
                : Array.from({ length: 1 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      sx={{
                        margin: "2rem 0.5rem",
                        width: "270px",
                        height: "300px",
                      }}
                    />
                  ))}
            </Grid>
          </Stack>
        </Box>
      ))}

      <ViewDetails
        open={open}
        setClose={setClose}
        imageUrl={
          viewDetails?.banner?.secureHttpPath ??
          viewDetails?.coverImage?.secureHttpPath
        }
        heading={viewDetails?.name}
        id={viewDetails?.id}
        cid={viewDetails?.cid}
        sid={viewDetails?.id}
        addOn={viewDetails?.addOn}
        servicedDetails={viewDetails?.aboutList}
        descriptions={viewDetails?.descriptions}
        amount={viewDetails?.amount}
        min={viewDetails?.min}
        subHeading={viewDetails?.subHeading}
        mrp={viewDetails?.mrp}
        coin={viewDetails?.cashbackSalontymCoins}
        aboutList={viewDetails?.aboutList}
        serviceCharge={viewDetails?.serviceCharge}
        productCost={viewDetails?.productCost}
        specialPrice={viewDetails?.specialPrice}
        specialMessage={viewDetails?.specialMessage}
        reloadCartItemsFlag={() => setUpdateCard(true)}
        reloadViewDetailsItem={reloadViewDetailsItem}
        setReloadViewDetailsItem={() => setReloadViewDetailsItem(false)}
        handleOpenViewDetailsRsOne={(sid: any) => handleOpenViewDetails(sid)}
      />
      <ViewDetailsRsOneContainer
        open={openRsOneDetails}
        handleCloseStoreDialog={() => setOpenRsOneDetails(false)}
        viewDetailsRsOneSid={viewDetailsRsOneSid}
      />
      <MakeYourOwnPackage
        open={openPackages}
        close={() => setOpenPackages(false)}
        sid={viewDetails?.id}
      />
      <BestPairedDesktopWraper
        openBestPaired={openBestPaired}
        setOpenBestPaired={setOpenBestPaired}
        recommendedServices={recommendedServices}
        handleViewDetails={(item: any) => handleViewDetails(item)}
        updateCard={updateCard}
        setUpdateCard={() => setUpdateCard(false)}
        reloadViewDetailsItem={() => setReloadViewDetailsItem(true)}
        handleOpenViewDetailsRsOne={(data: any, index: any) =>
          handleOpenViewDetailsRsOne(data, index)
        }
      />
    </>
  );
};

export default ServiceViewContainer;
